import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { FivePartReasoning } from "@/components/FivePartReasoning";
import { formatEnum } from "@/utils/formatEnum";
import { adviceTargetLabel, channelLabelForService } from "@/utils/adviceTargetLabel";
import { t } from "@/i18n";
import { FileText, Calendar, TrendingUp, AlertTriangle, Lightbulb, Target, Loader2 } from "lucide-react";

const CONF_STYLE = {
  HIGH: "bg-pos-green/20 text-pos-green border-pos-green/50",
  MEDIUM: "bg-warn-amber/20 text-warn-amber border-warn-amber/50",
  LOW: "bg-bg-panel-raised text-text-primary/85 border-border-hairline",
};

const fmtEUR = (n) =>
  n == null ? null : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function formatWeekLabel(week_start, week_end) {
  const s = new Date(week_start);
  const e = new Date(week_end);
  return `${s.toLocaleDateString("nl-NL", { day: "2-digit", month: "short" })} — ${e.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" })}`;
}

export default function WeeklyReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [activeSnapshotId, setActiveSnapshotId] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);
  const { activeOrg } = useAuth();
  const isDemo = activeOrg?.data_origin === "demo";
  const nav = useNavigate();

  const loadInbox = async () => {
    try {
      const r = await api.get("/weekly-report/inbox");
      setInbox(r.data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const rep = await api.get("/reports/weekly");
        setReport(rep.data);
      } catch (e) {
        setError(e?.response?.data?.detail || t("common.error_generic"));
      } finally {
        setLoading(false);
      }
    })();
    loadInbox();
  }, []);

  const loadSnapshot = async (id) => {
    setLoading(true);
    try {
      const r = await api.get(`/weekly-report/inbox/${id}`);
      setReport(r.data.payload);
      setActiveSnapshotId(id);
      if (!r.data.seen_at) {
        try { await api.post(`/weekly-report/inbox/${id}/mark-seen`); loadInbox(); }
        catch { /* silent */ }
      }
    } finally { setLoading(false); }
  };

  const showLive = async () => {
    setLoading(true);
    setActiveSnapshotId(null);
    try {
      const rep = await api.get("/reports/weekly");
      setReport(rep.data);
    } finally { setLoading(false); }
  };

  const takeSnapshot = async () => {
    setSnapshotting(true);
    try {
      await api.post("/weekly-report/snapshot");
      await loadInbox();
    } finally { setSnapshotting(false); }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center gap-2 text-text-muted py-24 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
        </div>
      </AppLayout>
    );
  }
  if (error || !report) {
    return (
      <AppLayout>
        <div className="p-8 rounded-xl border border-neg-red/40 bg-neg-red/10 text-neg-red text-sm">
          {error || "Rapport niet beschikbaar"}
        </div>
      </AppLayout>
    );
  }

  const { period, summary_nl, budget, week_totals, attention_items = [],
          opportunity_items = [], priorities_nl = [],
          insufficient_data_flags = [],
          google_facts = null, wallet_view = null,
          top_advices_g4 = [] } = report;
  const confKey = String(period.overall_confidence || "").toUpperCase();
  const confBadge = CONF_STYLE[confKey] || CONF_STYLE.LOW;
  // M128 G4 — na enrichment kan de oude "no_marketing_events"-vlag zijn
  // verwijderd terwijl Google-data WEL binnen is; extra check op
  // google_facts.week_days_covered / mtd zorgt dat we niet meer verkeerd
  // "geen data" tonen als de gedeelde bron wél metingen bevat.
  const hasGoogleData = !!google_facts && (
    (google_facts.week_days_covered || 0) > 0
    || (google_facts.google_mtd_spent_eur || 0) > 0);
  const hasEvents = hasGoogleData
    || !insufficient_data_flags.includes("no_marketing_events_this_week");
  const walletConfigured = !insufficient_data_flags.includes("wallet_not_configured");

  return (
    <AppLayout>
      <div className="mb-8" data-testid="weekly-report-page">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-accent-teal" />
            <h1 className="text-2xl font-display font-bold text-text-primary tracking-tight" data-testid="weekly-report-title">
              {t("weekly.title")}
            </h1>
            {isDemo && (
              <span data-testid="weekly-report-demo-badge"
                    className="text-[10px] font-bold px-2 py-0.5 rounded bg-warn-amber/20 text-warn-amber border border-warn-amber/40">
                Voorbeeldgegevens
              </span>
            )}
          </div>
        </div>

        {/* Section 0: period + confidence */}
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border-hairline bg-bg-panel p-4 mb-6"
             data-testid="weekly-report-period-block">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-text-muted" />
            <span data-testid="weekly-report-period-label" className="text-sm text-text-primary font-medium">
              {formatWeekLabel(period.week_start, period.week_end)}
            </span>
          </div>
          <div className="text-xs text-text-muted">
            {t("weekly.generated_at")}: <span data-testid="weekly-report-generated-at">{new Date(period.generated_at).toLocaleString("nl-NL")}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] text-text-muted font-semibold">
              {t("weekly.confidence_label")}
            </span>
            <span data-testid="weekly-report-confidence"
                  className={`text-[10px] font-bold px-2 py-0.5 rounded border ${confBadge}`}>
              {(() => {
                const nl = t(`enum.confidence_pill.${confKey}`);
                return nl && !nl.startsWith("enum.") ? nl : confKey;
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* KPI + main content wrapped for historie side-rail (Fase 3 Item 3) */}
      <div className="grid lg:grid-cols-[1fr_260px] gap-4 items-start"><div className="min-w-0">
      {/* Section 1: summary */}
      <SectionCard icon={TrendingUp} title="Deze week in het kort" testid="weekly-summary">
        <p className="text-sm text-text-primary leading-relaxed">{summary_nl}</p>
        {(() => {
          // KPI-strip — geeft voorkeur aan Google Ads per_day (echte gemeten
          // dagen deze week). Zonder Google-data valt hij terug op de oude
          // marketing_events-week_totals (demo-pad). Nooit `0` als fallback:
          // ontbrekende metrics tonen "onbekend".
          const gWk = google_facts?.week_totals || null;
          const fallback = hasEvents ? week_totals : null;
          const src = gWk || fallback;
          if (!src) return null;
          const fmtNum = (n) => n == null
            ? "onbekend"
            : Number(n).toLocaleString("nl-NL");
          const fmtMoney = (n) => n == null
            ? "onbekend"
            : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const spendVal = gWk ? gWk.spend_eur : src.spend;
          const isGoogle = !!gWk;
          const daysLbl = isGoogle
            ? google_facts?.coverage_label_nl
            : null;
          return (
            <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs"
                 data-testid="weekly-kpi-strip">
              <MiniStat label="Uitgegeven"
                        value={fmtMoney(spendVal)}
                        sub={daysLbl}
                        testid="weekly-kpi-spend" />
              <MiniStat label="Klikken"
                        value={fmtNum(src.clicks)}
                        sub={isGoogle ? "gemeten door Google Ads" : null}
                        testid="weekly-kpi-clicks" />
              <MiniStat label="Vertoningen"
                        value={fmtNum(src.impressions)}
                        sub={isGoogle ? "gemeten door Google Ads" : null}
                        testid="weekly-kpi-impressions" />
              <MiniStat label="Conversies"
                        value={fmtNum(src.conversions)}
                        sub={isGoogle
                          ? "gemeten door Google Ads, niet gelijk aan klanten"
                          : null}
                        testid="weekly-kpi-conversions" />
            </div>
          );
        })()}
      </SectionCard>

      {/* Section 2: budget — leest uit google_facts + wallet_view (gedeelde bron)
          zodra beschikbaar. Historische snapshots vallen terug op budget.* */}
      <SectionCard title="Budget & besteding" testid="weekly-budget">
        {!walletConfigured ? (
          <p data-testid="weekly-budget-empty" className="text-sm text-text-muted italic">{budget.note_nl}</p>
        ) : google_facts ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat
                label="Maandbudget"
                value={fmtEUR(budget.monthly_cap_eur)}
                testid="weekly-budget-monthly-cap"
              />
              <MiniStat
                label="Besteed deze week"
                value={fmtEUR(google_facts.spent_this_week_eur)}
                sub={google_facts.coverage_label_nl}
                testid="weekly-budget-week-spent"
              />
              <MiniStat
                label="Resterend deze maand"
                value={google_facts.total_spent_known
                  ? fmtEUR(google_facts.total_remaining_mtd_eur)
                  : "onbekend"}
                sub={`Google-plan: ${fmtEUR(google_facts.google_plan_remaining_mtd_eur)} van ${fmtEUR(google_facts.google_allocation_mtd_eur)} resterend`}
                testid="weekly-budget-remaining"
              />
              <MiniStat
                label="Verwachte weekuitgave"
                value={google_facts.projected_this_week_eur == null
                  ? "—"
                  : fmtEUR(google_facts.projected_this_week_eur)}
                sub={google_facts.projected_week_note_nl}
                testid="weekly-budget-projected-week"
              />
            </div>
            <p className="mt-2 text-xs text-text-muted italic"
               data-testid="weekly-budget-mtd-context">
              Google maand-tot-nu: € {Number(google_facts.google_mtd_spent_eur || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} van € {Number(google_facts.google_allocation_mtd_eur || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Google-plan.
              {!google_facts.total_spent_known && " Andere kanalen niet gekoppeld — totaal-resterend is onbekend."}
            </p>
          </>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Maandbudget" value={fmtEUR(budget.monthly_cap_eur)} />
            <MiniStat
              label="Besteed"
              value={budget.spend_verified === false ? t("ui.spend_unknown") : fmtEUR(budget.spent_eur)}
            />
            <MiniStat
              label="Resterend"
              value={budget.spend_verified === false ? t("ui.spend_unknown") : fmtEUR(budget.remaining_eur)}
            />
            <MiniStat label="Verwachte uitgaven aan het einde van de maand" value={fmtEUR(budget.projected_end_of_month_eur)} />
          </div>
        )}
        {walletConfigured && !google_facts && budget.note_nl && (
          <p className="mt-2 text-xs text-text-muted italic">{budget.note_nl}</p>
        )}
      </SectionCard>

      {/* Section 3: attention */}
      <SectionCard icon={AlertTriangle} title="Belangrijkste aandachtspunten" testid="weekly-attention">
        {attention_items.length === 0 ? (
          <p className="text-sm text-text-muted italic">
            {hasEvents
              ? "Geen urgente aandachtspunten deze week."
              : "Onvoldoende data om aandachtspunten te tonen. Koppel Google Ads via Gegevens en koppelingen."}
          </p>
        ) : (
          <div className="space-y-4">
            {attention_items.map((o) => <OpportunityCard key={o.id} opp={o} testid={`weekly-attention-item-${o.id}`} />)}
          </div>
        )}
      </SectionCard>

      {/* Section 4: opportunities */}
      <SectionCard icon={Lightbulb} title="Belangrijkste kansen" testid="weekly-opportunities">
        {opportunity_items.length === 0 ? (
          <p className="text-sm text-text-muted italic">
            {hasEvents
              ? "Nog geen concrete kansen deze week."
              : "Onvoldoende data om kansen te tonen. Koppel Google Ads via Gegevens en koppelingen."}
          </p>
        ) : (
          <div className="space-y-4">
            {opportunity_items.map((o) => <OpportunityCard key={o.id} opp={o} testid={`weekly-opportunity-item-${o.id}`} />)}
          </div>
        )}
      </SectionCard>

      {/* Section 5: priorities — klikbaar naar detail-drawer via identity_key
          zodra g4-adviezen aanwezig zijn; anders eerlijke lege-staat. */}
      <SectionCard icon={Target} title="Prioriteiten komende week" testid="weekly-priorities">
        {top_advices_g4.length > 0 ? (
          <ul className="space-y-2" data-testid="weekly-priorities-list">
            {top_advices_g4.slice(0, 5).map((a) => {
              const ZEK_NL = { hoog: "Hoog", middel: "Middel", laag: "Laag" };
              const zek = ((a.body || {}).zekerheid || "middel").toLowerCase();
              const channelNL = channelLabelForService(a.service);
              const tgt = adviceTargetLabel(a.service, a.advice_rule_id, a.target_entity_id);
              return (
                <li key={a.identity_key}
                    data-testid={`weekly-priority-g4-${a.identity_key}`}>
                  <a href={`/ghost-ledger?open=${encodeURIComponent(a.identity_key)}`}
                     data-testid={`weekly-priority-link-${a.identity_key}`}
                     className="block rounded border border-border-hairline hover:border-accent-teal/40 bg-bg-panel hover:bg-bg-panel-raised p-3 transition-colors">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[9px] text-accent-teal font-bold border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded">
                        {channelNL}
                      </span>
                      <span className="text-sm font-semibold text-text-primary leading-snug">
                        {a.title || (a.body || {}).wat_ik_zie}
                      </span>
                    </div>
                    {tgt && (
                      <div className="text-[11px] text-text-muted mb-1"
                           data-testid={`weekly-priority-target-${a.identity_key}`}>
                        <span className="text-text-primary/85">{tgt.label}</span>
                        {tgt.path && (
                          <span className="ml-1 text-text-subtle">({tgt.path})</span>
                        )}
                      </div>
                    )}
                    {(a.body || {}).wat_ik_adviseer && (
                      <div className="text-xs text-text-muted mt-1 leading-relaxed">
                        {(a.body || {}).wat_ik_adviseer}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-panel-raised text-text-muted border border-border-hairline">
                        Zekerheid: {ZEK_NL[zek] || "Middel"}
                      </span>
                      <span className="text-[9px] text-accent-teal ml-auto">
                        Bekijk waarom →
                      </span>
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>
        ) : priorities_nl.length === 0 ? (
          <p className="text-sm text-text-muted italic">
            Nog geen prioriteiten — deze verschijnen zodra NEXORA data heeft om op te bouwen.
          </p>
        ) : (
          <ol className="space-y-2 list-decimal list-inside">
            {priorities_nl.map((p, i) => (
              <li key={i} data-testid={`weekly-priority-${i}`}
                  className="text-sm text-text-primary leading-relaxed">
                {p}
              </li>
            ))}
          </ol>
        )}
      </SectionCard>

      <div className="mt-6 text-[11px] text-text-muted flex items-center gap-2">
        <span data-testid="weekly-ghost-note">{t("weekly.ghost_note")}</span>
      </div>
      </div>
      <WeeklyHistoryRail
        inbox={inbox}
        activeSnapshotId={activeSnapshotId}
        onSelectSnapshot={loadSnapshot}
        onShowLive={showLive}
        onTakeSnapshot={takeSnapshot}
        snapshotting={snapshotting}
      />
      </div>
    </AppLayout>
  );
}

function WeeklyHistoryRail({ inbox, activeSnapshotId, onSelectSnapshot,
                              onShowLive, onTakeSnapshot, snapshotting }) {
  return (
    <aside data-testid="weekly-history-rail"
            className="rounded-xl border border-border-hairline bg-bg-panel p-3 lg:sticky lg:top-16">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] text-text-muted font-semibold"
             style={{ letterSpacing: "0.08em" }}>
          {t("weekly.history_title")}
        </h3>
      </div>
      <button
        type="button"
        onClick={onTakeSnapshot}
        disabled={snapshotting}
        data-testid="weekly-take-snapshot-btn"
        className="w-full mb-2 h-8 rounded-md border border-accent-teal/50 bg-accent-teal/10 hover:bg-accent-teal/20 disabled:opacity-50 text-accent-teal text-[11px] transition-colors duration-exec"
      >
        {snapshotting ? "…" : t("weekly.generate_snapshot")}
      </button>
      <button
        type="button"
        onClick={onShowLive}
        data-testid="weekly-show-live-btn"
        className={`w-full mb-3 h-8 rounded-md border text-[11px] transition-colors duration-exec ${ activeSnapshotId == null ? "border-accent-teal/50 bg-accent-teal/10 text-accent-teal" : "border-border-hairline text-text-muted hover:text-text-primary hover:border-accent-teal/40" }`}
      >
        {t("weekly.live_label")}
      </button>
      {inbox.length === 0 ? (
        <div className="text-xs text-text-muted italic">{t("weekly.history_empty")}</div>
      ) : (
        <ul className="space-y-1" data-testid="weekly-history-list">
          {inbox.map((s) => {
            const isActive = s.id === activeSnapshotId;
            const range = formatWeekLabel(s.week_start, s.week_end);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSelectSnapshot(s.id)}
                  data-testid={`weekly-history-item-${s.id}`}
                  className={`w-full text-left px-2 py-1.5 rounded-md border transition-colors duration-exec ${ isActive ? "bg-bg-panel-raised border-accent-teal/40" : "bg-bg-panel border-transparent hover:bg-bg-panel-raised hover:border-border-hairline" }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-text-primary num truncate">{range}</span>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${ s.seen_at ? "bg-tier-c-grey" : "bg-accent-teal" }`} title={s.seen_at ? t("weekly.snapshot_seen") : t("weekly.snapshot_unseen")} />
                  </div>
                  {s.summary_line && (
                    <div className="text-[10px] text-text-muted mt-0.5 truncate">
                      {s.summary_line}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

function SectionCard({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border-hairline bg-bg-panel p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-text-muted" />}
        <h2 className="text-sm text-text-primary/85 font-bold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function MiniStat({ label, value, sub, testid }) {
  return (
    <div className="rounded-md border border-border-hairline bg-bg-panel p-3"
         data-testid={testid}>
      <div className="text-[10px] text-text-muted font-semibold">{label}</div>
      <div className="text-lg text-text-primary tabular-nums mt-1">{value ?? "—"}</div>
      {sub && (
        <div className="text-[10px] text-text-subtle mt-0.5 leading-snug">
          {sub}
        </div>
      )}
    </div>
  );
}

function OpportunityCard({ opp, testid }) {
  const confKey = opp.confidence >= 0.7 ? "HIGH" : opp.confidence >= 0.5 ? "MEDIUM" : "LOW";
  // M128 G4 — geen rawlabels (ADS_PACING_OVERSHOOT/GSC_LOW_CTR_PAGE) meer op
  // de kaart. Alleen kanaal-badge (Google Ads / Google Search Console) + B1-
  // titel + kort target-pad om vier identieke titels onderscheidbaar te
  // maken. `advice_rule_id` staat in het Bewijs-blok van de detail-drawer.
  const channelNL = channelLabelForService(opp.service);
  const tgt = adviceTargetLabel(opp.service, opp.advice_rule_id, opp.target_entity_id);
  return (
    <div data-testid={testid} className="rounded-md border border-border-hairline bg-bg-panel p-4">
      <div className="mb-2 flex items-center gap-2 flex-wrap">
        <span data-testid={`${testid}-channel-badge`}
              className="text-[10px] text-accent-teal font-bold border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded">
          {channelNL}
        </span>
        <span className="text-sm font-semibold text-text-primary">{opp.title}</span>
      </div>
      {tgt && (
        <div className="mb-2 text-[11px] text-text-muted"
             data-testid={`${testid}-target-label`}>
          <span className="text-text-primary/85">{tgt.label}</span>
          {tgt.path && (
            <span className="ml-1 text-text-subtle">({tgt.path})</span>
          )}
        </div>
      )}
      <FivePartReasoning
        reasoning={opp.reasoning_summary || {}}
        confidence={confKey}
        scoreBreakdown={opp.opportunity_score_breakdown}
        compact
        testidPrefix={`${testid}-reasoning`}
      />
    </div>
  );
}
